class PlanOperationsCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  // Handle save plan action
  async handleSavePlan(data) {
    console.log("💾 PlanOperationsCapability: Handling save plan:", data);

    // Extract the data to save
    if (data.keyObjectives) {
      this.setters.setObjectivesStatement(data.keyObjectives);
    }

    // Mark as unsaved to trigger auto-save
    this.setters.setIsSaved(false);

    // Trigger save if available
    if (this.savePlan && typeof this.savePlan === 'function') {
      setTimeout(() => {
        this.savePlan();
      }, 100);
    }

    return { 
      success: true, 
      message: "Plan saved successfully",
      updatedFields: Object.keys(data)
    };
  }

  // Handle updating key objectives specifically
  async handleUpdateKeyObjectives(data) {
    console.log("📝 PlanOperationsCapability: Handling key objectives update:", data);

    if (data.keyObjectives && Array.isArray(data.keyObjectives)) {
      this.setters.setObjectivesStatement(data.keyObjectives);
      this.setters.setIsSaved(false);

      return { 
        success: true, 
        message: `Updated ${data.keyObjectives.length} key objectives`,
        objectives: data.keyObjectives
      };
    }

    return { 
      success: false, 
      message: "Invalid objectives data provided" 
    };
  }

  async handleUpdateObjectives(data) {
    console.log("🎯 PlanOperationsCapability: Updating objectives", data);

    try {
      const content = data.content || data.value || data.text || data.objectives;

      if (!content) {
        console.error("❌ No content provided for objectives update");
        return { success: false, message: "No content provided for objectives update" };
      }

      // Handle both array and string formats
      let objectives;
      if (Array.isArray(content)) {
        objectives = content;
      } else if (typeof content === 'string') {
        objectives = content.split('\n').map(obj => obj.trim()).filter(obj => obj.length > 0);
      } else {
        objectives = [content.toString()];
      }

      console.log("🔧 Setting objectives in React state:", objectives);
      this.setters.setObjectivesStatement(objectives);
      this.setters.setIsSaved(false); // Mark as unsaved

      // Force a small delay to ensure React state updates
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log("✅ Objectives updated successfully:", objectives);

      return { 
        success: true, 
        message: "Objectives updated successfully",
        updatedValue: objectives
      };
    } catch (error) {
      console.error("❌ Error updating objectives:", error);
      return { success: false, message: error.message };
    }
  }

  async handleUpdateSummary(data) {
    const content = data.short_summary || data.shortSummary || data.summary || data.content || data.value || data.text || data.newValue;
    if (content) {
      this.setters.setShortSummary(content);
      this.setters.setIsSaved(false);
      return { success: true, message: "Summary updated successfully", updatedValue: content };
    }
    return { success: false, message: "No summary provided" };
  }

  async handleUpdateProblemStatement(data) {
    console.log("🎯 PlanOperationsCapability: Updating problem statement", data);

    try {
      const content = data.problem_statement || data.problemStatement || data.content || data.value || data.text;

      if (!content) {
        console.error("❌ No content provided for problem statement update");
        return { success: false, message: "No content provided for problem statement update" };
      }

      console.log("🔧 Setting problem statement in React state:", content);
      this.setters.setProblemStatement(content);
      this.setters.setIsSaved(false); // Mark as unsaved

      // Force a small delay to ensure React state updates
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log("✅ Problem statement updated successfully:", content);

      return { 
        success: true, 
        message: "Problem statement updated successfully",
        updatedValue: content
      };
    } catch (error) {
      console.error("❌ Error updating problem statement:", error);
      return { success: false, message: error.message };
    }
  }

  async handleUpdateGoalStatement(data) {
    console.log("🎯 PlanOperationsCapability: Updating goal statement", data);

    try {
      const content = data.goal_statement || data.goalStatement || data.content || data.value || data.text;

      if (!content) {
        console.error("❌ No content provided for goal statement update");
        return { success: false, message: "No content provided for goal statement update" };
      }

      console.log("🔧 Setting goal statement in React state:", content);
      this.setters.setGoalStatement(content);
      this.setters.setIsSaved(false); // Mark as unsaved

      // Force a small delay to ensure React state updates
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log("✅ Goal statement updated successfully:", content);

      return { 
        success: true, 
        message: "Goal statement updated successfully",
        updatedValue: content
      };
    } catch (error) {
      console.error("❌ Error updating goal statement:", error);
      return { success: false, message: error.message };
    }
  }

  async handleUpdateDesignChallenge(data) {
    console.log("🎯 PlanOperationsCapability: Updating design challenge", data);

    try {
      const content = data.design_challenge || data.designChallenge || data.content || data.value || data.text;

      if (!content) {
        console.error("❌ No content provided for design challenge update");
        return { success: false, message: "No content provided for design challenge update" };
      }

      console.log("🔧 Setting design challenge in React state:", content);
      this.setters.setCurrentDesignChallenge(content);
      this.setters.setIsSaved(false);

      await new Promise(resolve => setTimeout(resolve, 100));

      console.log("✅ Design challenge updated successfully:", content);

      return { 
        success: true, 
        message: "Design challenge updated successfully",
        updatedValue: content
      };
    } catch (error) {
      console.error("❌ Error updating design challenge:", error);
      return { success: false, message: error.message };
    }
  }

  async handleUpdateTargetStatement(data) {
    console.log("🎯 PlanOperationsCapability: Updating target statement", data);

    try {
      const content = data.content || data.value || data.text || data.targetStatement || data.target_statement;

      if (!content) {
        console.error("❌ No content provided for target statement update");
        return { success: false, message: "No content provided for target statement update" };
      }

      console.log("🔧 Setting target statement in React state:", content);
      this.setters.setTargetStatement(content);
      this.setters.setIsSaved(false); // Mark as unsaved

      // Force a small delay to ensure React state updates
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log("✅ Target statement updated successfully:", content);

      return { 
        success: true, 
        message: "Target statement updated successfully",
        updatedValue: content
      };
    } catch (error) {
      console.error("❌ Error updating target statement:", error);
      return { success: false, message: error.message };
    }
  }

  async handleUpdateDeliverables(data) {
    const content = data.deliverables || data.content || data.value || data.text || data.newValue;
    if (content) {
      this.setters.setDeliverablesStatement(content);
      this.setters.setIsSaved(false);
      return { success: true, message: "Deliverables updated successfully" };
    }
    return { success: false, message: "No deliverables provided" };
  }

  async handleUpdateMetrics(data) {
    const content = data.metrics || data.content || data.value || data.text || data.newValue;
    if (content) {
      this.setters.setMetricsStatement(content);
      this.setters.setIsSaved(false);
      return { success: true, message: "Metrics updated successfully" };
    }
    return { success: false, message: "No metrics provided" };
  }

  async handleUpdateStakeholders(data) {
    const content = data.stakeholders || data.content || data.value || data.text || data.newValue;
    if (content) {
      this.setters.setStakeholdersCustomersInvolvement(content);
      this.setters.setIsSaved(false);
      return { success: true, message: "Stakeholders updated successfully" };
    }
    return { success: false, message: "No stakeholders provided" };
  }

  async handleUpdateExperienceDesignPrinciples(data) {
    const content = data.experience_design_principles || data.experienceDesignPrinciples || data.content || data.value || data.text || data.newValue;
    if (content) {
      this.setters.setExperienceDesignPrinciples(content);
      this.setters.setIsSaved(false);
      return { success: true, message: "Experience design principles updated successfully" };
    }
    return { success: false, message: "No experience design principles provided" };
  }

  async handleUpdatePrograms(data) {
    console.log('🎯 PlanOperationsCapability: Updating programs with data:', data);

    try {
      if (data.programs && Array.isArray(data.programs)) {
        // Update the final program sequence
        this.setters.setFinalProgramSequence(data.programs);
        this.setters.setSelectedPrograms(data.programs);

        // Recalculate total duration
        const totalDuration = data.programs.reduce((sum, program) => {
          return sum + (program.totalProgramLength || program.duration || 0);
        }, 0);
        this.setters.setTotalDuration(totalDuration);

        this.setters.setIsSaved(false);

        return { 
          success: true, 
          message: `Updated ${data.programs.length} programs successfully`,
          data: data.programs
        };
      }

      return { success: false, message: 'No valid programs data provided' };
    } catch (error) {
      console.error('Error updating programs:', error);
      return { success: false, message: `Failed to update programs: ${error.message}` };
    }
  }

  async handleUpdateStrategyComponent(data, action = null) {
    console.log('🎯 PlanOperationsCapability: Updating strategy component with data:', data);
    console.log('🎯 PlanOperationsCapability: Action context:', action);

    try {
      // CRITICAL FIX: Determine capability from action first, then override with explicit data
      let capabilityId = action?.capability || action?.capabilityId;
      let componentType = data.componentType || data.component || data.field;
      const newValue = data.newValue || data.content || data.value || data.text;

      console.log(`🔧 Strategy component routing DEBUG:`, {
        capabilityId,
        componentType,
        newValue: newValue?.substring(0, 50) + '...',
        actionKeys: action ? Object.keys(action) : 'no action',
        dataKeys: Object.keys(data || {})
      });

      // CRITICAL: If we have a capability ID, route directly to the specific handler
      if (capabilityId) {
        console.log(`🔧 Direct capability routing: ${capabilityId}`);

        switch (capabilityId) {
          case 'update_goal_statement':
            console.log('✅ Direct routing to GOAL STATEMENT handler');
            return await this.handleUpdateGoalStatement({ content: newValue });

          case 'update_problem_statement':
            console.log('✅ Direct routing to PROBLEM STATEMENT handler');
            return await this.handleUpdateProblemStatement({ content: newValue });

          case 'update_target_statement':
            console.log('✅ Direct routing to TARGET STATEMENT handler');
            return await this.handleUpdateTargetStatement({ content: newValue });

          case 'update_objectives':
            console.log('✅ Direct routing to OBJECTIVES handler');
            return await this.handleUpdateObjectives({ content: newValue });

          case 'update_deliverables':
            console.log('✅ Direct routing to DELIVERABLES handler');
            return await this.handleUpdateDeliverables({ content: newValue });

          case 'update_metrics':
            console.log('✅ Direct routing to METRICS handler');
            return await this.handleUpdateMetrics({ content: newValue });

          case 'update_stakeholders':
            console.log('✅ Direct routing to STAKEHOLDERS handler');
            return await this.handleUpdateStakeholders({ content: newValue });

          case 'update_summary':
            console.log('✅ Direct routing to SUMMARY handler');
            return await this.handleUpdateSummary({ content: newValue });

          case 'update_experience_design_principles':
            console.log('✅ Direct routing to EXPERIENCE DESIGN PRINCIPLES handler');
            return await this.handleUpdateExperienceDesignPrinciples({ content: newValue });

          default:
            console.error(`❌ CRITICAL: Unknown capability: ${capabilityId}`);
            return { 
              success: false, 
              message: `Unknown capability: ${capabilityId}` 
            };
        }
      }

      // FALLBACK: If no capability ID, try to determine from component type
      if (componentType) {
        console.log(`🔧 Fallback routing by component type: ${componentType}`);

        switch (componentType.toLowerCase()) {
          case 'metrics':
          case 'success_metrics':
          case 'metricsstatement':
            console.log('✅ Fallback routing to METRICS handler');
            return await this.handleUpdateMetrics({ content: newValue });

          case 'objectives':
          case 'key_objectives':
          case 'objectivesstatement':
            console.log('✅ Fallback routing to OBJECTIVES handler');
            return await this.handleUpdateObjectives({ content: newValue });

          case 'deliverables':
          case 'deliverablesstatement':
            console.log('✅ Fallback routing to DELIVERABLES handler');
            return await this.handleUpdateDeliverables({ content: newValue });

          case 'stakeholders':
          case 'stakeholderscustomersinvolvement':
            console.log('✅ Fallback routing to STAKEHOLDERS handler');
            return await this.handleUpdateStakeholders({ content: newValue });

          case 'goal_statement':
          case 'goalstatement':
          case 'goal':
            console.log('✅ Fallback routing to GOAL STATEMENT handler');
            return await this.handleUpdateGoalStatement({ content: newValue });

          case 'problem_statement':
          case 'problemstatement':
          case 'problem':
            console.log('✅ Fallback routing to PROBLEM STATEMENT handler');
            return await this.handleUpdateProblemStatement({ content: newValue });

          case 'target_statement':
          case 'targetstatement':
          case 'target':
            console.log('✅ Fallback routing to TARGET STATEMENT handler');
            return await this.handleUpdateTargetStatement({ content: newValue });

          case 'summary':
          case 'short_summary':
          case 'shortsummary':
            console.log('✅ Fallback routing to SUMMARY handler');
            return await this.handleUpdateSummary({ content: newValue });

          case 'experience_design_principles':
          case 'experiencedesignprinciples':
          case 'design_principles':
            console.log('✅ Fallback routing to EXPERIENCE DESIGN PRINCIPLES handler');
            return await this.handleUpdateExperienceDesignPrinciples({ content: newValue });

          default:
            console.error(`❌ CRITICAL: Unknown component type: ${componentType}`);
            return { 
              success: false, 
              message: `Unknown component type: ${componentType}` 
            };
        }
      }

      console.error('❌ CRITICAL: No capability ID or component type provided');
      return { 
        success: false, 
        message: 'No capability ID or component type provided for strategy component update' 
      };

    } catch (error) {
      console.error('❌ Error updating strategy component:', error);
      return { 
        success: false, 
        message: `Failed to update strategy component: ${error.message}` 
      };
    }
  }

  // CONVERSATION MODE CAPABILITIES
  // Handle generating plan creation CTA for conversation mode
  async handleGeneratePlanCta(data) {
    console.log('🎯 PlanOperationsCapability: Generating plan creation CTA', data);

    try {
      const { brief, message, workflow_id, recommended_product } = data;
      
      // Determine which product to recommend (default to quick_plan if not specified)
      const productId = workflow_id || recommended_product || "quick_plan";
      
      // Product-specific CTA configuration
      const productConfigs = {
        quick_plan: {
          title: "Create Quick Strategy",
          description: "Bring the right specialists together to decide if this idea is worth building, in minutes",
          workflow_id: "quick_plan",
          estimated_duration: "2-3 minutes"
        },
        autonomous_design_strategy: {
          title: "Create Full Strategy",
          description: "A decision-driven roadmap that defines what must be proven — and what decision each proof unlocks — before building begins",
          workflow_id: "autonomous_design_strategy",
          estimated_duration: "5-8 minutes"
        }
      };
      
      // Get the config for the recommended product
      const productConfig = productConfigs[productId] || productConfigs.quick_plan;
      
      return {
        success: true,
        message: `Ready to create your ${productConfig.title.toLowerCase()}`,
        show_plan_cta: true,
        cta_data: {
          title: productConfig.title,
          description: productConfig.description,
          brief_content: brief || message || "User brief",
          workflow_id: productConfig.workflow_id,
          estimated_duration: productConfig.estimated_duration,
          action_type: "create_plan"
        }
      };
    } catch (error) {
      console.error('❌ Error generating plan CTA:', error);
      return {
        success: false,
        message: `Failed to generate plan CTA: ${error.message}`
      };
    }
  }

  // Handle brief refinement suggestions
  async handleRefineBrief(data) {
    console.log('🎯 PlanOperationsCapability: Refining brief', data);

    try {
      const { original_brief, refinement_suggestions } = data;
      
      return {
        success: true,
        message: "Brief refinement suggestions generated",
        refinement_data: {
          original: original_brief,
          suggestions: refinement_suggestions || [],
          action_type: "refine_brief"
        }
      };
    } catch (error) {
      console.error('❌ Error refining brief:', error);
      return {
        success: false,
        message: `Failed to refine brief: ${error.message}`
      };
    }
  }

  // Handle approach suggestions
  async handleSuggestApproach(data) {
    console.log('🎯 PlanOperationsCapability: Suggesting approach', data);

    try {
      const { context, approach_options } = data;
      
      return {
        success: true,
        message: "Approach suggestions generated",
        approach_data: {
          context: context,
          options: approach_options || [],
          action_type: "suggest_approach"
        }
      };
    } catch (error) {
      console.error('❌ Error suggesting approach:', error);
      return {
        success: false,
        message: `Failed to suggest approach: ${error.message}`
      };
    }
  }
}

module.exports = PlanOperationsCapability;